/**
 * RecipeCard Component
 *
 * Displays a recipe card for the recipe list grid.
 * Shows image, title, cooking time, calories, rating, tags, and storage info.
 */

import { Link } from '@inertiajs/react';
import { RatingStars } from './RatingStars';
import type { RecipeSummary, FoodTag } from '@/types/food';
import { getTagStyle } from '@/types/food';

interface RecipeCardProps {
  recipe: RecipeSummary;
}

/**
 * Format date for display
 */
function formatLastMade(dateString: string | null | undefined): string | null {
  if (!dateString) return null;

  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Get gradient background for placeholder image
 */
function getPlaceholderGradient(id: number): string {
  const gradients = [
    'from-violet-500 to-purple-600',
    'from-pink-500 to-rose-600',
    'from-cyan-500 to-blue-600',
    'from-amber-500 to-orange-600',
    'from-emerald-500 to-teal-600',
    'from-fuchsia-500 to-pink-600',
  ];
  return gradients[id % gradients.length];
}

/**
 * Get food icon for placeholder
 */
function getPlaceholderIcon(): React.ReactElement {
  return (
    <svg
      className="w-12 h-12 text-white/80"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 8.25v-1.5m0 1.5c-1.355 0-2.697.056-4.024.166C6.845 8.51 6 9.473 6 10.608v2.513m6-4.87c1.355 0 2.697.055 4.024.165C17.155 8.51 18 9.473 18 10.608v2.513m-3-4.87v-1.5m-6 1.5v-1.5m12 9.75l-1.5.75a3.354 3.354 0 01-3 0 3.354 3.354 0 00-3 0 3.354 3.354 0 01-3 0 3.354 3.354 0 00-3 0 3.354 3.354 0 01-3 0L3 16.5m15-3.38a48.474 48.474 0 00-6-.37c-2.032 0-4.034.125-6 .37m12 0c.39.049.777.102 1.163.16 1.07.16 1.837 1.094 1.837 2.175v5.17c0 .62-.504 1.124-1.125 1.124H4.125A1.125 1.125 0 013 20.625v-5.17c0-1.08.768-2.014 1.837-2.174A47.78 47.78 0 016 13.12M12.265 3.11a.375.375 0 11-.53 0L12 2.845l.265.265zm-3 0a.375.375 0 11-.53 0L9 2.845l.265.265zm6 0a.375.375 0 11-.53 0L15 2.845l.265.265z"
      />
    </svg>
  );
}

/**
 * Tag chip component
 */
function TagChip({ tag }: { tag: FoodTag }) {
  const style = getTagStyle(tag.namespace);

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}
    >
      {tag.name}
    </span>
  );
}

export function RecipeCard({ recipe }: RecipeCardProps) {
  const lastMade = formatLastMade(recipe.lastPreparedAt);
  const gradientClass = getPlaceholderGradient(recipe.id);

  return (
    <Link
      href={`/food/recipes/${recipe.id}`}
      className="block bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5"
      data-testid={`recipe-card-${recipe.id}`}
    >
      {/* Image Section */}
      <div className="relative h-44 overflow-hidden">
        {recipe.photoUrl ? (
          <img
            src={recipe.photoUrl}
            alt={recipe.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div
            className={`w-full h-full bg-gradient-to-br ${gradientClass} flex items-center justify-center`}
          >
            {getPlaceholderIcon()}
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-1">
          {recipe.title}
        </h3>

        {/* Meta info */}
        <div className="flex items-center gap-4 mb-3 text-sm text-gray-600">
          <span className="flex items-center gap-1">
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {recipe.cookingTimeMinutes} min
          </span>

          {recipe.caloriesPerPortion && (
            <span className="flex items-center gap-1">
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1A3.75 3.75 0 0012 18z"
                />
              </svg>
              {recipe.caloriesPerPortion} kcal
            </span>
          )}

          <RatingStars rating={recipe.averageRating} size="sm" />
        </div>

        {/* Tags */}
        {recipe.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {recipe.tags.slice(0, 3).map((tag) => (
              <TagChip key={tag.id} tag={tag} />
            ))}
            {recipe.tags.length > 3 && (
              <span className="text-xs text-gray-500 self-center">
                +{recipe.tags.length - 3} more
              </span>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100 text-xs">
          <span className="text-gray-500">
            {lastMade ? `Last made: ${lastMade}` : 'Never made'}
          </span>

          <div className="flex gap-1.5">
            {recipe.freezable && (
              <span className="px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 font-semibold uppercase tracking-wide">
                Freezable
              </span>
            )}
            {recipe.eatImmediately && (
              <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold uppercase tracking-wide">
                Eat Fresh
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
